import {
  ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { AppConfig } from '../../../../../app.config';
import { HelpersService } from '../../../../../services/helpers.service';
import { ServiceRequestService } from '../../../../../services/servicerequest.service';
import { JobcardService } from '../../../../../services/jobcard.service';
import { ChecklistService } from '../../../../../services/checklist.service';
import { ReceiptTemplateService } from '../services/receipt-template.service';
import { ReceiptTemplatePrintService } from '../services/receipt-template-print.service';
import { TranslateService } from '@ngx-translate/core';
import { ReceiptTemplate } from '../models/receipt-template.model';
import { CanvasDataMap, mapJobCardToSampleData } from '../models/merge-tag.model';
import { TemplateCanvasComponent } from '../editor/template-canvas/template-canvas.component';

@Component({
  selector: 'app-receipt-template-print-page',
  templateUrl: './receipt-template-print-page.component.html',
  styleUrls: ['./receipt-template-print-page.component.scss'],
})
export class ReceiptTemplatePrintPageComponent implements OnInit, OnDestroy {

  @ViewChild(TemplateCanvasComponent) private canvasRef?: TemplateCanvasComponent;

  public template: ReceiptTemplate | null = null;
  public jobData: CanvasDataMap | null = null;
  public loading = true;
  public errorMessage = '';

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private appConfig: AppConfig,
    private templateService: ReceiptTemplateService,
    private printService: ReceiptTemplatePrintService,
    private serviceRequestService: ServiceRequestService,
    private jobcardService: JobcardService,
    private checklistService: ChecklistService,
    private helpersService: HelpersService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    const templateId = this.route.snapshot.paramMap.get('templateId') ?? '';
    const jobCardId  = Number(this.route.snapshot.paramMap.get('jobCardId'));

    forkJoin([
      this.templateService.getTemplateById(templateId).pipe(first()),
      this.serviceRequestService.getManageJobCardDetails(jobCardId),
    ]).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([template, jobcard]) => {
          if (!template) {
            this.errorMessage = this.translate.instant('bikeshop.receipt_template.print_page.error_template_not_found');
            this.loading = false;
            return;
          }
          this.template = template;
          this.loadJobCardData(jobcard);
        },
        error: () => {
          this.errorMessage = this.translate.instant('bikeshop.receipt_template.print_page.error_load_failed');
          this.loading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Data loading ─────────────────────────────────────────────────────────

  /**
   * Loads all supporting data for the job card (QR code URL, service checklist,
   * image attachments) then maps everything to a CanvasDataMap and triggers
   * the print dialog.  Mirrors the logic in TemplateEditorComponent.loadTestJobData()
   * but runs automatically on page load rather than on user demand.
   */
  private loadJobCardData(jobcard: any): void {
    this.jobcardService.getJobCardQRIdentifier(jobcard.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result) => {
          jobcard.qrCodeURL =
            `${window.location.origin}/cyclist/jobcardAttachments/${jobcard.id}` +
            `?QRCodeIdentifier=${result.qrCodeIdentifier}&Theme=${jobcard.partnerID}`;

          const showPreService =
            this.helpersService.getPartnerData().Print_ShowOnlyPreServiceCheck;

          this.checklistService
            .getServiceChecklist(jobcard.partnerID, jobcard.id, showPreService)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (checklist) => {
                const checklists = checklist;
                //.filter(
                //  (x: any) => x.mandatoryForAllServices || x.linkedToServiceTypes,
                //);

                this.jobcardService.getJobCardAttachments(jobcard.id)
                  .pipe(takeUntil(this.destroy$))
                  .subscribe(
                    (attachments: any[]) => {
                      const imageUrls = (attachments ?? [])
                        .filter((a: any) => (a.mediaType ?? '').indexOf('image') >= 0)
                        .map((a: any) => a.mediaURL as string)
                        .filter(Boolean);

                      jobcard.checklists = checklists;
                      const data = mapJobCardToSampleData(jobcard, this.appConfig.getStatusList(), this.helpersService.getPartnerData(), this.translate);
                      this.finalise(
                        imageUrls.length > 0 ? { ...data, images: imageUrls } : data,
                      );
                    },
                    () => {
                      // Attachment fetch failed — print without images
                      jobcard.checklists = checklists;
                      this.finalise(mapJobCardToSampleData(jobcard, this.appConfig.getStatusList(), this.helpersService.getPartnerData(), this.translate));
                    },
                  );
              },
              () => {
                // Checklist fetch failed — print without checklist
                this.finalise(mapJobCardToSampleData(jobcard, this.appConfig.getStatusList(), this.helpersService.getPartnerData(), this.translate));
              },
            );
        },
        () => {
          // QR fetch failed — print without QR URL
          this.finalise(mapJobCardToSampleData(jobcard, this.appConfig.getStatusList(), this.helpersService.getPartnerData(), this.translate));
        },
      );
  }

  /** Sets job data, marks loading complete, and schedules the print capture. */
  private finalise(data: CanvasDataMap): void {
    this.jobData = data;
    this.loading = false;
    this.cdr.detectChanges();
    // Allow Angular one tick to render the canvas elements before capture.
    setTimeout(() => this.triggerPrint(), 250);
  }

  /** Captures the rendered canvas and opens the print dialog in a separate window. */
  private triggerPrint(): void {
    const canvasEl = document.querySelector('.tc-canvas') as HTMLElement | null;
    if (!canvasEl || !this.template) return;

    // Force a synchronous measurement pass so auto-height elements (tables,
    // free-text blocks) are correctly sized before the outerHTML is captured.
    this.canvasRef?.forceRemeasure();

    const opened = this.printService.print(canvasEl, this.template.name);
    if (!opened) {
      this.errorMessage = this.translate.instant('bikeshop.receipt_template.print_page.error_popup_blocked');
    }

    // Close this rendering tab once the print window is open.
    if (opened) {
      setTimeout(() => window.close(), 500);
    }
  }
}
